import clsx from 'clsx';
import * as React from 'react';

export type AutoSizeTextareaProps = {
    value: string;
    onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
    /**
     * The maximum number of rows to display.
     */
    maxRows?: number;
    /**
     * Class name for the textarea element.
     */
    textareaClassName?: string;
} & Omit<React.ComponentPropsWithoutRef<'textarea'>, 'value' | 'onChange'>;

/**
 * Auto size textarea component.
 * @example
 * <AutoSizeTextarea value={value} onChange={onChange} rows={2} maxRows={4} />
 */
export const AutoSizeTextarea = React.forwardRef(function AutoSizeTextarea(
    props: AutoSizeTextareaProps,
    ref: React.ForwardedRef<HTMLTextAreaElement>
): React.ReactElement {
    const { className, textareaClassName, style, rows = 1, maxRows, ...rest } = props;

    return (
        <div
            className={clsx('relative size-full overflow-hidden', className)}
            style={{
                ...style,
                position: 'relative',
                color: 'inherit',
                padding: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
                letterSpacing: 'inherit',
                minHeight: `${rows * 1}lh`,
            }}
        >
            <div
                aria-hidden
                className="pointer-events-none relative h-full min-w-0.5 select-none whitespace-pre-wrap break-words align-top opacity-0"
                style={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                    minHeight: `${rows * 1}lh`,
                    maxHeight: maxRows !== undefined ? `${maxRows * 1}lh` : undefined,
                }}
            >
                {(props.value || props.placeholder || '').split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && <br />}
                        {line || ' '}
                    </React.Fragment>
                ))}
            </div>
            <textarea
                ref={ref}
                className={clsx(
                    'absolute inset-0 size-full resize-none overflow-hidden bg-transparent',
                    textareaClassName
                )}
                style={{
                    color: 'inherit',
                    padding: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                }}
                {...rest}
            />
        </div>
    );
});
